import { z } from 'zod';
import { assemblyReportStatusSchema } from './assembly-report.schema';

/**
 * Montage-Einsatz (Modul assembly, ADR 0010). Vier Werte, bewusst ohne "läuft"
 * (2026-08-25): der Zwischenschritt zwischen "bestätigt" und "abgeschlossen"
 * brachte keine Entscheidung mit sich, nur Pflegeaufwand. "CONFIRMED" statt
 * des früheren "SCHEDULED" — das Alt-System (base44) nannte diesen Stand
 * bereits "Bestätigt".
 */
export const assemblyTaskStatusSchema = z.enum([
  'PLANNED',
  'CONFIRMED',
  'DONE',
  'CANCELLED',
]);
export type AssemblyTaskStatus = z.infer<typeof assemblyTaskStatusSchema>;

export const createAssemblyTaskSchema = z.object({
  title: z.string().trim().min(1).max(200),
  status: assemblyTaskStatusSchema.default('PLANNED'),
  projectId: z.string().uuid().optional(),
  /** Einsatz-Kategorie (verwaltbar, ADR 0053). */
  categoryId: z.string().uuid().optional(),
  location: z.string().trim().max(200).optional(),
  /** Interne Monteure als HR-Mitarbeiter (ADR 0053). */
  technicianEmployeeIds: z.array(z.string().uuid()).max(20).optional(),
  /** Freitext für externe/zusätzliche Monteure (Fremdfirmen). */
  technicians: z.string().trim().max(500).optional(),
  /**
   * Beauftragte Montage-Partnerfirma (ADR 0085, Stufe 2). Gesetzt ⇒ der Einsatz
   * erscheint im Partnerportal dieses Partners. Höchstens einer je Einsatz.
   * Der `technicians`-Freitext bleibt davon unberührt — er benennt Personen,
   * dieses Feld die beauftragte Firma.
   */
  partnerAccountId: z.string().uuid().optional(),
  plannedStart: z.string().datetime().optional(),
  plannedEnd: z.string().datetime().optional(),
  /** An-/Abreise der Monteure — kann vom Arbeitszeitraum abweichen. */
  travelStart: z.string().datetime().optional(),
  travelEnd: z.string().datetime().optional(),
  notes: z.string().trim().max(5000).optional(),
});
export type CreateAssemblyTaskInput = z.infer<typeof createAssemblyTaskSchema>;

export const updateAssemblyTaskSchema = z
  .object({
    title: z.string().trim().min(1).max(200),
    status: assemblyTaskStatusSchema,
    projectId: z.string().uuid().nullable(),
    categoryId: z.string().uuid().nullable(),
    location: z.string().trim().max(200).nullable(),
    technicianEmployeeIds: z.array(z.string().uuid()).max(20),
    technicians: z.string().trim().max(500).nullable(),
    /** Montage-Partner zuordnen oder die Zuordnung lösen (`null`), ADR 0085. */
    partnerAccountId: z.string().uuid().nullable(),
    plannedStart: z.string().datetime().nullable(),
    plannedEnd: z.string().datetime().nullable(),
    travelStart: z.string().datetime().nullable(),
    travelEnd: z.string().datetime().nullable(),
    notes: z.string().trim().max(5000).nullable(),
  })
  .partial();
export type UpdateAssemblyTaskInput = z.infer<typeof updateAssemblyTaskSchema>;

export const assemblyTaskResponseSchema = z.object({
  id: z.string().uuid(),
  title: z.string(),
  status: assemblyTaskStatusSchema,
  projectId: z.string().uuid().nullable(),
  projectCode: z.string().nullable(),
  categoryId: z.string().uuid().nullable(),
  /** Zur Laufzeit aufgelöst (Umbenennen wirkt sofort); null = Kategorie weg. */
  categoryName: z.string().nullable(),
  categoryColor: z.string().nullable(),
  location: z.string().nullable(),
  technicianEmployeeIds: z.array(z.string().uuid()),
  /** Namens-Snapshot in ID-Reihenfolge. */
  technicianNames: z.array(z.string()),
  technicians: z.string().nullable(),
  partnerAccountId: z.string().uuid().nullable(),
  /** Zur Laufzeit aufgelöst; null = kein Partner bzw. Partner entfernt. */
  partnerName: z.string().nullable(),
  plannedStart: z.string().datetime().nullable(),
  plannedEnd: z.string().datetime().nullable(),
  travelStart: z.string().datetime().nullable(),
  travelEnd: z.string().datetime().nullable(),
  completedAt: z.string().datetime().nullable(),
  notes: z.string().nullable(),
  /**
   * Stand des Baustellenberichts (2026-10-05, Spalte „Bericht" der Einsatzliste):
   * null = noch kein Bericht angelegt. Zur Laufzeit aufgelöst, kein Snapshot.
   */
  reportStatus: assemblyReportStatusSchema.nullable(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});
export type AssemblyTaskResponse = z.infer<typeof assemblyTaskResponseSchema>;

/** Anzeige der Monteure: HR-Namen + Freitext (Externe) kombiniert. */
export function assemblyTechniciansLabel(task: {
  technicianNames: string[];
  technicians: string | null;
}): string {
  return [...task.technicianNames, task.technicians ?? '']
    .map((value) => value.trim())
    .filter(Boolean)
    .join(', ');
}

/** Eine Einsatz-Teilmenge, wie die Doppelbelegungs-Prüfung sie braucht. */
export interface AssemblyOverlapTask {
  id: string;
  title: string;
  technicianEmployeeIds: string[];
  plannedStart: string | null;
  plannedEnd: string | null;
}

export interface AssemblyOverlap {
  taskId: string;
  message: string;
}

/**
 * Doppelbelegungs-Prüfung beim Verschieben eines Einsatzes (ADR 0053):
 * meldet Einsätze, die sich im Zeitraum überschneiden UND mindestens einen
 * Monteur teilen. Reine Warnung — gespeichert wird nach Bestätigung.
 */
export function findAssemblyOverlaps(
  tasks: AssemblyOverlapTask[],
  moved: { id: string; technicianEmployeeIds: string[]; start: string; end: string },
): AssemblyOverlap[] {
  const movedStart = new Date(moved.start).getTime();
  const movedEnd = new Date(moved.end).getTime();
  const movedCrew = new Set(moved.technicianEmployeeIds);
  if (movedCrew.size === 0) return [];

  const overlaps: AssemblyOverlap[] = [];
  for (const task of tasks) {
    if (task.id === moved.id) continue;
    if (!task.plannedStart) continue;
    const start = new Date(task.plannedStart).getTime();
    const end = task.plannedEnd ? new Date(task.plannedEnd).getTime() : start;
    if (end < movedStart || start > movedEnd) continue;
    const shared = task.technicianEmployeeIds.filter((id) => movedCrew.has(id));
    if (shared.length === 0) continue;
    overlaps.push({
      taskId: task.id,
      message: `Doppelbelegung mit „${task.title}" (gemeinsame Monteure).`,
    });
  }
  return overlaps;
}
